import { FC, useEffect, useState } from "react";
import Grid from "@mui/material/Unstable_Grid2";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import Box from "@mui/material/Box";
import { EstateDocument } from "src/types/snugdocs";
import { StripeElement } from "src/sections/dashboard/stripe/stripe-element";
import { PaymentRequiredData } from "src/hooks/snug/use-payments";
import { StripePaymentIntent } from "src/types/stripe";
import { CircularProgress, Divider } from "@mui/material";
import useHousehold from "src/contexts/household-context";
import { useParams, useNavigate } from "react-router-dom";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";

import {
  Dialog,
  DialogContent,
  Button,
} from "@mui/material";

interface ExpressPaymentDialogProps {
  open: boolean;
  stateEstateDoc: EstateDocument | null;
  expressPaywillSignup?: boolean;
  product: string | null; // "WILL" or "TRUST"
  trustType?: "joint" | "individual" | null; // For TRUST products: "joint" for joint trust, "individual" for individual trust(s)
  paymentOption?: "HOUSEHOLD" | "INDIVIDUAL" | null; // "HOUSEHOLD" for 2x (both spouses), "INDIVIDUAL" for 1x (single person)
  ud_id?: string | null; // User data ID for payment operations
  // Stripe-related props (passed from parent context)
  paymentRequiredData?: PaymentRequiredData | null;
  paymentRequiredLoading?: boolean;
  paymentsSaving?: boolean;
  currentPaymentIntent?: StripePaymentIntent | null;
  handleCreateOrUpdatePaymentIntent: (
    ud_id: string | null,
    paymentIntentId: string | null,
    product: string
  ) => Promise<void>;
  handleClearPaymentIntent: () => void;
  handleGetPaymentRequired: (
    ud_id: string | null,
    product: string
  ) => Promise<PaymentRequiredData | null>;
  createSetupIntent: (ud_id: string) => Promise<{ client_secret: string; stripe_connect_id?: string } | null>;
  createTrialSubscription: (ud_id: string, payment_method_id: string, price_id?: string) => Promise<any>;
  onClose: () => void;
  onSuccessRedirect?: string; // Optional redirect path (e.g., "/express")
  handleEstateDocPartialUpdate: (
    estateDocumentId: string | null,
    ud_id: string | null,
    data: any
  ) => Promise<EstateDocument>;
  handleChange: (key: string, value: any) => void;
  setPaywallSuccess: (value: boolean) => void;
  setPaymentRequired: (value: boolean) => void;
}

export const ExpressPaymentDialog: FC<ExpressPaymentDialogProps> = (props) => {
  const {
    open,
    onClose,
    onSuccessRedirect,
    stateEstateDoc,
    product,
    trustType,
    paymentOption,
    ud_id: propUdId,
    paymentRequiredData,
    paymentRequiredLoading,
    currentPaymentIntent,
    handleCreateOrUpdatePaymentIntent,
    handleClearPaymentIntent,
    handleGetPaymentRequired,
    handleEstateDocPartialUpdate,
    handleChange,
    setPaywallSuccess,
    setPaymentRequired,
  } = props;
  const { selectedHousehold } = useHousehold();
  const { ud_id: paramsUdId } = useParams();
  const navigate = useNavigate();
  // const [paymentSuccess, setPaymentSuccess] = useState<boolean>(false);
  const [localPaymentRequired, setLocalPaymentRequired] =
    useState<boolean>(false);

  // Determine ud_id: use prop if provided, otherwise from params
  const ud_id = propUdId !== undefined ? propUdId : paramsUdId || null;

  // Show (2x) based on paymentOption - this is derived from the user's explicit
  // choice in the plan selector (e.g., "both" for wills, "two_individual" for trusts)
  const showTwoX = paymentOption === "HOUSEHOLD";

  const enforceAnnual =
    selectedHousehold?.professional_group?.enforce_annual_payment_check ?? false;
  const updatesCopy = enforceAnnual
    ? "1 year of unlimited updates"
    : "Unlimited updates";

  // Get membership price from professional group (for display purposes)
  // const membershipPrice =
  //   selectedHousehold?.professional_group?.default_membership_price || 11999; // Default to $119.99

  // Fetch payment required data when dialog opens
  useEffect(() => {
    if (open && ud_id && product && handleGetPaymentRequired) {
      handleGetPaymentRequired(ud_id, product); // Document creation paywall - always INDIVIDUAL
    }
  }, [open, ud_id, product, handleGetPaymentRequired]);

  useEffect(() => {
    if (paymentRequiredData && product) {
      // Calculate actual amount due after accounting for credits
      // NOTE: API prices (will_price/trust_price) already have payment history applied
      const basePrice =
        product === "WILL"
          ? paymentRequiredData.will_price
          : paymentRequiredData.trust_price;

      const amountDue = Math.max(
        0,
        basePrice -
          (paymentRequiredData.credit_available || 0)
      );

      setLocalPaymentRequired(amountDue > 0);
    }
  }, [paymentRequiredData, product]);

  useEffect(() => {
    if (!localPaymentRequired && currentPaymentIntent?.id) {
      handleClearPaymentIntent();
    }
  }, [
    localPaymentRequired,
    currentPaymentIntent?.id,
    handleClearPaymentIntent,
  ]);

  const handlePaymentSuccess = async () => {
    // setPaymentSuccess(true);
    if (setPaywallSuccess) {
      setPaywallSuccess(true);  // Immediate UX bypass
    }
    // Clear parent's payment required state so user can submit
    if (setPaymentRequired) {
      setPaymentRequired(false);
    }
    // Refetch payment data to update state after successful payment
    if (handleGetPaymentRequired && ud_id && product) {
      await handleGetPaymentRequired(ud_id, product); // Document creation paywall - always INDIVIDUAL
    }
    // Auto-close dialog after showing success message
    setTimeout(() => {
      onClose();
      // Redirect if specified (e.g., to /express after onboarding)
      if (onSuccessRedirect) {
        navigate(onSuccessRedirect);
      }
    }, 2500);
  };

  const allow_trusts =
    !selectedHousehold?.household_role?.block_trust &&
    selectedHousehold?.professional_group?.allow_trusts;
  const allow_wills =
    !selectedHousehold?.household_role?.block_will &&
    selectedHousehold?.professional_group?.allow_wills;
  const allow_quitclaim_deed =
    selectedHousehold?.professional_group?.allow_quitclaim_deed ?? false;

  useEffect(() => {
    if (
      selectedHousehold?.household_role?.block_trust &&
      ud_id &&
      product === "TRUST"
    ) {
      handleChange("type", "WILL");
      handleEstateDocPartialUpdate(stateEstateDoc?.id || null, ud_id || null, {
        type: "WILL",
      });
    }
    if (
      selectedHousehold?.household_role?.block_will &&
      ud_id &&
      product === "WILL"
    ) {
      handleChange("type", "TRUST");
      handleEstateDocPartialUpdate(stateEstateDoc?.id || null, ud_id || null, {
        type: "TRUST",
      });
    }
  }, [
    allow_trusts,
    allow_wills,
    selectedHousehold,
    stateEstateDoc?.id,
    ud_id,
    product,
    handleChange,
    handleEstateDocPartialUpdate,
  ]);

  return (
    <Dialog
      open={open} //open
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogContent
        sx={{
          position: "relative",
          minHeight: 600,
          display: "flex",
          flexDirection: "column",
          p: 0,
        }}
      >
        {/* Document payment */}
        {localPaymentRequired && (
          <Grid container spacing={0} sx={{ flex: 1, minHeight: "100%" }}>
            <Grid
              xs={12}
              md={4}
              sx={{ display: "flex", flexDirection: "column", backgroundColor: "#faf9f8", p: 4, borderRight: "1px solid #e7e7e7" }}
            >
              <Stack
                justifyContent={"space-between"}
                sx={{ height: "100%", flex: 1 }}
              >
                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    {product === "WILL" ? (
                      <Typography variant="h4">
                        Will Plan
                      </Typography>
                    ) : (
                      <Typography variant="h4">
                        Trust Plan
                      </Typography>
                    )}
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    You are purchasing a{" "}
                    {product === "WILL" ? `Will` : `Trust`}-based estate
                    plan with {enforceAnnual ? "1 year of unlimited updates" : "unlimited updates"}.
                  </Typography>

                  <Divider />

                  {/* Pricing summary */}
                  <Stack sx={{ mb: 2 }} spacing={0}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Order Summary
                    </Typography>
                    <Stack direction="row" justifyContent="space-between">
                      {product === "WILL" ? (
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Will package</Typography>
                      ) : (
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Trust package</Typography>
                      )}

                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        $
                        {((product === "WILL"
                          ? (paymentRequiredData?.will_price || 0) +
                            (paymentRequiredData?.credit_available ?? 0)
                          : (paymentRequiredData?.trust_price || 0) +
                            (paymentRequiredData?.credit_available ?? 0)) / 100).toLocaleString()}
                      </Typography>
                    </Stack>

                    {(paymentRequiredData?.credit_available ?? 0) > 0 && (
                      <>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            Credits available
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            $
                            {((paymentRequiredData?.credit_available ?? 0) / 100).toLocaleString()}
                          </Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Total due</Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            $
                            {((product === "WILL"
                              ? paymentRequiredData?.will_price || 0
                              : paymentRequiredData?.trust_price || 0) / 100).toLocaleString()}
                          </Typography>
                        </Stack>
                      </>
                    )}

                    <Box sx={{ mt: 3 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: "text.primary", mb: 1, display: "block" }}
                      >
                        Included Documents
                      </Typography>
                      {product === "WILL" ? (
                        <Stack spacing={0.5}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 0.75,
                            }}
                          >
                            <CheckOutlinedIcon
                              sx={{
                                color: "success.main",
                                fontSize: 16,
                                mt: 0.25,
                              }}
                            />
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              Last Will & Testament{showTwoX ? " (2x)" : ""}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 0.75,
                            }}
                          >
                            <CheckOutlinedIcon
                              sx={{
                                color: "success.main",
                                fontSize: 16,
                                mt: 0.25,
                              }}
                            />
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              Health Care Directive{showTwoX ? " (2x)" : ""}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 0.75,
                            }}
                          >
                            <CheckOutlinedIcon
                              sx={{
                                color: "success.main",
                                fontSize: 16,
                                mt: 0.25,
                              }}
                            />
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              Power of Attorney{showTwoX ? " (2x)" : ""}
                            </Typography>
                          </Box>
                        </Stack>
                      ) : (
                        <Stack spacing={0.5}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 0.75,
                            }}
                          >
                            <CheckOutlinedIcon
                              sx={{
                                color: "success.main",
                                fontSize: 16,
                                mt: 0.25,
                              }}
                            />
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              {trustType === "joint" ? "Joint " : ""}Revocable Living Trust{showTwoX && trustType !== "joint" ? " (2x)" : ""}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 0.75,
                            }}
                          >
                            <CheckOutlinedIcon
                              sx={{
                                color: "success.main",
                                fontSize: 16,
                                mt: 0.25,
                              }}
                            />
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              Pour Over Will{showTwoX ? " (2x)" : ""}
                            </Typography>
                          </Box>
                          {allow_quitclaim_deed && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 0.75,
                              }}
                            >
                              <CheckOutlinedIcon
                                sx={{
                                  color: "success.main",
                                  fontSize: 16,
                                  mt: 0.25,
                                }}
                              />
                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                Quitclaim Deed
                              </Typography>
                            </Box>
                          )}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 0.75,
                            }}
                          >
                            <CheckOutlinedIcon
                              sx={{
                                color: "success.main",
                                fontSize: 16,
                                mt: 0.25,
                              }}
                            />
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              Health Care Directive{showTwoX ? " (2x)" : ""}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 0.75,
                            }}
                          >
                            <CheckOutlinedIcon
                              sx={{
                                color: "success.main",
                                fontSize: 16,
                                mt: 0.25,
                              }}
                            />
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              Power of Attorney{showTwoX ? " (2x)" : ""}
                            </Typography>
                          </Box>
                        </Stack>
                      )}
                    </Box>

                    <Box sx={{ mt: 3, mb:2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: "text.primary", mb: 1, display: "block" }}
                      >
                        Key Features
                      </Typography>
                      <Stack spacing={0.5}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 0.75,
                          }}
                        >
                          <CheckOutlinedIcon
                            sx={{
                              color: "success.main",
                              fontSize: 16,
                              mt: 0.25,
                            }}
                          />
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {updatesCopy}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 0.75,
                          }}
                        >
                          <CheckOutlinedIcon
                            sx={{
                              color: "success.main",
                              fontSize: 16,
                              mt: 0.25,
                            }}
                          />
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            Secure digital vault
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 0.75,
                          }}
                        >
                          <CheckOutlinedIcon
                            sx={{
                              color: "success.main",
                              fontSize: 16,
                              mt: 0.25,
                            }}
                          />
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            Sharing with family and advisors
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Stack>

                  {/* <Stack sx={{ mb: 2 }} spacing={0}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Premium Membership
                    </Typography>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">
                        Due today (30 days free)
                      </Typography>
                      <Typography variant="body2">$0</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">
                        Due February 1, 2026
                      </Typography>
                      <Typography variant="body2">
                        ${(membershipPrice / 100).toFixed(2)}
                      </Typography>
                    </Stack>

                    <Accordion
                      disableGutters
                      elevation={0}
                      sx={{
                        "&:before": { display: "none" },
                        bgcolor: "transparent",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <ExpandMoreIcon
                            sx={{ color: "primary.main", fontSize: 16 }}
                          />
                        }
                        sx={{
                          px: 0,
                          minHeight: "auto",
                          "& .MuiAccordionSummary-content": { my: 0.5 },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "primary.main" }}
                        >
                          Premium features
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 0, pt: 0 }}>
                        <Stack spacing={1}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1,
                            }}
                          >
                            <CheckOutlinedIcon
                              sx={{
                                color: "success.main",
                                fontSize: 20,
                                mt: 0.25,
                              }}
                            />
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {enforceAnnual
                                  ? "Will and Trust updates for 1 year"
                                  : "Unlimited Will and Trust updates"}
                              </Typography>
                              <Tooltip
                                title={
                                  enforceAnnual
                                    ? "Update your Will or Trust documents for 1 year to keep your plans up to date."
                                    : "Unlimited lifetime updates to your Will or Trust documents makes it simple to keep your plans up to date."
                                }
                                arrow
                              >
                                <WorkspacePremiumIcon
                                  sx={{
                                    fontSize: 16,
                                    color: "text.secondary",
                                    cursor: "pointer",
                                  }}
                                />
                              </Tooltip>
                            </Stack>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1,
                            }}
                          >
                            <CheckOutlinedIcon
                              sx={{
                                color: "success.main",
                                fontSize: 20,
                                mt: 0.25,
                              }}
                            />
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                Secure Digital Vault
                              </Typography>
                              <Tooltip
                                title="Store important documents, digital assets, and financial information in a secure digital vault."
                                arrow
                              >
                                <WorkspacePremiumIcon
                                  sx={{
                                    fontSize: 16,
                                    color: "text.secondary",
                                    cursor: "pointer",
                                  }}
                                />
                              </Tooltip>
                            </Stack>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1,
                            }}
                          >
                            <CheckOutlinedIcon
                              sx={{
                                color: "success.main",
                                fontSize: 20,
                                mt: 0.25,
                              }}
                            />
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                Emergency Access Cards
                              </Typography>
                              <Tooltip
                                title="Physical wallet cards that provide your trusted contacts with emergency access to critical information."
                                arrow
                              >
                                <WorkspacePremiumIcon
                                  sx={{
                                    fontSize: 16,
                                    color: "text.secondary",
                                    cursor: "pointer",
                                  }}
                                />
                              </Tooltip>
                            </Stack>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1,
                            }}
                          >
                            <CheckOutlinedIcon
                              sx={{
                                color: "success.main",
                                fontSize: 20,
                                mt: 0.25,
                              }}
                            />
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                Family Sharing
                              </Typography>
                              <Tooltip
                                title="Securely share your account and documents with family members and beneficiaries."
                                arrow
                              >
                                <WorkspacePremiumIcon
                                  sx={{
                                    fontSize: 16,
                                    color: "text.secondary",
                                    cursor: "pointer",
                                  }}
                                />
                              </Tooltip>
                            </Stack>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1,
                            }}
                          >
                            <CheckOutlinedIcon
                              sx={{
                                color: "success.main",
                                fontSize: 20,
                                mt: 0.25,
                              }}
                            />
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                Video Legacy Messages
                              </Typography>
                              <Tooltip
                                title="Record and store personal video messages for your loved ones."
                                arrow
                              >
                                <WorkspacePremiumIcon
                                  sx={{
                                    fontSize: 16,
                                    color: "text.secondary",
                                    cursor: "pointer",
                                  }}
                                />
                              </Tooltip>
                            </Stack>
                          </Box>
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  </Stack> */}
                </Stack>

                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  sx={{
                    py: 0.3,
                    px: 1.5,
                    fontSize: "0.75rem",
                    borderRadius: 0.6,
                    border: "1px solid #cfcfcf",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                    backgroundColor: "white",
                    "&:hover": { backgroundColor: "#f5f5f5" },
                  }}
                  onClick={() => {
                    onClose();
                    navigate("/onboarding?step=personal_plan");
                  }}
                >
                  Change your selected plan
                </Button>
              </Stack>
            </Grid>
            <Grid xs={12} md={8} sx={{ p: 4 }}>
              <Stack spacing={3}>
                {paymentRequiredLoading ? (
                  // a centered circular progress spinner
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  paymentRequiredData && (
                    <StripeElement
                      ud_id={ud_id || null}
                      willOrTrust={product || ""}
                      handlePaymentSuccess={handlePaymentSuccess}
                      paymentRequired={localPaymentRequired}
                      handleCreateOrUpdatePaymentIntent={
                        handleCreateOrUpdatePaymentIntent
                      }
                      currentPaymentIntent={currentPaymentIntent || null}
                      amount={
                        product === "WILL"
                          ? paymentRequiredData?.will_price || 0
                          : paymentRequiredData?.trust_price || 0
                      }
                      onCancel={onClose}
                      paymentScope={selectedHousehold?.household_role?.paywall_option === "signup" ? selectedHousehold?.head_of_household?.estate_plan_foundation === "joint_trust" ? "INDIVIDUAL" : "HOUSEHOLD" : "INDIVIDUAL"}
                    />
                  )
                )}
              </Stack>
            </Grid>
          </Grid>
        )}
        {/* {false && !localPaymentRequired && paymentSuccess && (
          <>
            <Grid container spacing={3}>
              <Grid xs={12} md={4}>
                <Stack direction="column" spacing={2}>
                  <SvgIcon fontSize="large" sx={{ color: "#3e8a28" }}>
                    <CheckCircleSolidIcon />
                  </SvgIcon>
                  <Typography color="text.primary" variant="h6">
                    Thank you for your payment
                  </Typography>
                </Stack>
                <Typography
                  color="text.secondary"
                  sx={{ mt: 1, flex: 1 }}
                  variant="body2"
                >
                  Your order was completed successfully.
                </Typography>
              </Grid>
              <Grid xs={12} md={8}>
                <Typography variant="subtitle1">
                  Your purchase includes a year of Total Planning.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    pt: 3,
                  }}
                >
                  <Stack
                    spacing={1}
                    sx={{
                      flexGrow: 1,
                      pl: { xs: 0, sm: 2 },
                    }}
                  >
                    <Stack
                      alignItems="center"
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: "center",
                        display: "flex",
                      }}
                    >
                      <SvgIcon color="success">
                        <CheckIcon />
                      </SvgIcon>
                      <Typography sx={{ fontWeight: 500 }} variant="body2">
                        Store and maintain your essential estate planning
                        documents
                      </Typography>
                    </Stack>
                    <Stack
                      alignItems="center"
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: "center",
                        display: "flex",
                      }}
                    >
                      <SvgIcon color="success">
                        <CheckIcon />
                      </SvgIcon>
                      <Typography sx={{ fontWeight: 500 }} variant="body2">
                        Capture your stories and words of wisdom with video
                        messages
                      </Typography>
                    </Stack>
                    <Stack
                      alignItems="center"
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: "center",
                        display: "flex",
                      }}
                    >
                      <SvgIcon color="success">
                        <CheckIcon />
                      </SvgIcon>
                      <Typography sx={{ fontWeight: 500 }} variant="body2">
                        Share your plan with family for access anywhere, anytime
                      </Typography>
                    </Stack>
                    <Stack
                      alignItems="center"
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: "center",
                        display: "flex",
                      }}
                    >
                      <SvgIcon color="success">
                        <CheckIcon />
                      </SvgIcon>
                      <Typography sx={{ fontWeight: 500 }} variant="body2">
                        Organize life's details in one place to leave a legacy,
                        not a mess
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </>
        )} */}
        {/* {false &&
          !localPaymentRequired &&
          !paymentSuccess &&
          (paymentRequiredData?.credit_available ?? 0) > 0 && (
            <>
              <Grid container spacing={3}>
                <Grid xs={12} md={4}>
                  <Stack direction="column" spacing={2}>
                    <SvgIcon fontSize="large" sx={{ color: "#3e8a28" }}>
                      <CheckCircleSolidIcon />
                    </SvgIcon>
                    <Typography color="text.primary" variant="h6">
                      No payment required
                    </Typography>
                  </Stack>
                  <Typography
                    color="text.secondary"
                    sx={{ mt: 1, flex: 1 }}
                    variant="body2"
                  >
                    This order will be covered by your credits.
                  </Typography>
                </Grid>
                <Grid xs={12} md={8}>
                  <Typography variant="subtitle1">
                    Your purchase includes a year of Total Planning.
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      flexGrow: 1,
                      pt: 3,
                    }}
                  >
                    <Stack
                      spacing={1}
                      sx={{
                        flexGrow: 1,
                        pl: { xs: 0, sm: 2 },
                      }}
                    >
                      <Stack
                        alignItems="center"
                        direction="row"
                        spacing={1}
                        sx={{
                          alignItems: "center",
                          display: "flex",
                        }}
                      >
                        <SvgIcon color="success">
                          <CheckIcon />
                        </SvgIcon>
                        <Typography sx={{ fontWeight: 500 }} variant="body2">
                          Store and maintain your essential estate planning
                          documents
                        </Typography>
                      </Stack>
                      <Stack
                        alignItems="center"
                        direction="row"
                        spacing={1}
                        sx={{
                          alignItems: "center",
                          display: "flex",
                        }}
                      >
                        <SvgIcon color="success">
                          <CheckIcon />
                        </SvgIcon>
                        <Typography sx={{ fontWeight: 500 }} variant="body2">
                          Capture your stories and words of wisdom with video
                          messages
                        </Typography>
                      </Stack>
                      <Stack
                        alignItems="center"
                        direction="row"
                        spacing={1}
                        sx={{
                          alignItems: "center",
                          display: "flex",
                        }}
                      >
                        <SvgIcon color="success">
                          <CheckIcon />
                        </SvgIcon>
                        <Typography sx={{ fontWeight: 500 }} variant="body2">
                          Share your plan with family for access anywhere,
                          anytime
                        </Typography>
                      </Stack>
                      <Stack
                        alignItems="center"
                        direction="row"
                        spacing={1}
                        sx={{
                          alignItems: "center",
                          display: "flex",
                        }}
                      >
                        <SvgIcon color="success">
                          <CheckIcon />
                        </SvgIcon>
                        <Typography sx={{ fontWeight: 500 }} variant="body2">
                          Organize life's details in one place to leave a
                          legacy, not a mess
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                </Grid>
              </Grid>
            </>
          )} */}
      </DialogContent>
    </Dialog>
  );
};
